import { useState, useEffect } from "react";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Bell, Mail, MessageSquare, Shield, Package, FileText } from "lucide-react";

const TYPE_META = {
  appointment_reminder: { label: "Appointment Reminder", color: "bg-blue-100 text-blue-800", icon: Bell },
  booking_confirmation: { label: "Booking Confirmation", color: "bg-emerald-100 text-emerald-800", icon: FileText },
  low_stock: { label: "Low Stock Alert", color: "bg-amber-100 text-amber-800", icon: Package },
  claim_update: { label: "Claim Update", color: "bg-violet-100 text-violet-800", icon: Shield },
  claim_update_bulk: { label: "Bulk Claim Update", color: "bg-violet-100 text-violet-800", icon: Shield },
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "appointment_reminder", label: "Reminders" },
  { key: "booking_confirmation", label: "Confirmations" },
  { key: "low_stock", label: "Stock Alerts" },
  { key: "claim_update", label: "Claim Updates" },
];

export default function Notifications() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    const url = filter ? `/notifications?type=${filter}` : "/notifications";
    api.get(url)
      .then(r => setItems(r.data))
      .catch(err => toast({ title: "Error", description: formatError(err), variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [filter, toast]);

  const stats = {
    total: items.length,
    real: items.filter(i => i.mode === "real").length,
    mocked: items.filter(i => i.mode === "mocked").length,
    internal: items.filter(i => i.mode === "internal").length,
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    if (isToday) return `Today ${time}`;
    return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${time}`;
  };

  const channelIcon = (ch) => {
    if (ch === "email") return <Mail size={12} />;
    if (ch === "sms") return <MessageSquare size={12} />;
    return null;
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-sans">Notifications History</h1>
        <p className="text-slate-500 text-sm mt-1">Audit trail of all outbound notifications and internal events</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <StatTile label="Total" value={stats.total} color="text-slate-700" bg="bg-slate-100" />
        <StatTile label="Real" value={stats.real} color="text-emerald-700" bg="bg-emerald-50" />
        <StatTile label="Mocked (Demo)" value={stats.mocked} color="text-amber-700" bg="bg-amber-50" />
        <StatTile label="Internal" value={stats.internal} color="text-violet-700" bg="bg-violet-50" />
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              filter === f.key
                ? "bg-emerald-700 text-white border-emerald-700"
                : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"
            }`}
            data-testid={`filter-type-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Type</TableHead>
              <TableHead className="font-semibold text-slate-700">Recipient</TableHead>
              <TableHead className="font-semibold text-slate-700">Channel</TableHead>
              <TableHead className="font-semibold text-slate-700">Mode</TableHead>
              <TableHead className="font-semibold text-slate-700">Detail</TableHead>
              <TableHead className="font-semibold text-slate-700">By</TableHead>
              <TableHead className="font-semibold text-slate-700">When</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">Loading...</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">
                <Bell size={32} className="mx-auto mb-2 opacity-30" />
                <p>No notifications yet</p>
              </TableCell></TableRow>
            ) : items.map(n => {
              const meta = TYPE_META[n.type] || { label: n.type, color: "bg-slate-100 text-slate-700", icon: Bell };
              const Icon = meta.icon;
              return (
                <TableRow key={n.id} data-testid={`notification-row-${n.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Icon size={14} className="text-slate-500" />
                      <Badge className={`text-xs ${meta.color}`}>{meta.label}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <p className="font-medium text-slate-900">{n.recipient_name || "—"}</p>
                    <p className="text-xs text-slate-500 truncate max-w-48">{n.recipient_contact || ""}</p>
                  </TableCell>
                  <TableCell>
                    {n.channel && n.channel !== "internal" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-600 capitalize">
                        {channelIcon(n.channel)}
                        {n.channel}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${
                      n.mode === "real" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                      n.mode === "mocked" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                      "bg-violet-50 text-violet-700 border border-violet-200"
                    }`}>
                      {n.mode || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {n.type === "low_stock" && `${n.low_stock_count} low, ${n.expiring_count || 0} expiring`}
                    {n.type === "appointment_reminder" && <span>{formatDoctorName(n.doctor_name)} · {n.appointment_date}</span>}
                    {n.type === "booking_confirmation" && <span>{formatDoctorName(n.doctor_name)} · {n.appointment_date}</span>}
                    {n.type === "claim_update" && <span>{n.from_status || "—"} → <b>{n.to_status}</b></span>}
                    {n.type === "claim_update_bulk" && <span>→ <b>{n.to_status}</b></span>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">{n.triggered_by || "system"}</TableCell>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtTime(n.at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatTile({ label, value, color, bg }) {
  return (
    <div className={`${bg} rounded-xl p-4`}>
      <p className="text-xs text-slate-600">{label}</p>
      <p className={`text-2xl font-bold font-sans ${color}`}>{value}</p>
    </div>
  );
}
